import { X, GraduationCap, ListChecks, Target, Sparkles, FileText, Smile, CalendarClock, NotebookPen, Map, Share2, ShieldCheck, Puzzle, Printer, Sprout, ChevronRight, Compass, Layers } from "lucide-react";

const SECTIONS = [
  {
    key: "classes",
    icon: GraduationCap,
    tint: "#81B29A",
    title: "1. Classes & students",
    body: "Create a class, then add your pupils. Tick the ASD box for any child who needs an Autism Support & Development Profile.",
  },
  {
    key: "assess",
    icon: ListChecks,
    tint: "#6C8EBF",
    title: "2. Assess on a 1-9 scale",
    body: "Open a pupil, pick a subject, and tap a level 1-9 for each skill. Colours instantly show who's emerging, developing or secure.",
  },
  {
    key: "targets",
    icon: Target,
    tint: "#C08552",
    title: "3. Set priority targets",
    body: "Add SMART targets for each child and mark them as you go. This is the heart of your planning.",
  },
  {
    key: "ai",
    icon: Sparkles,
    tint: "#9A7AA0",
    title: "AI helper",
    body: "Stuck for words? Let AI suggest or refine curriculum-aligned targets and summaries, then edit to fit the child.",
  },
  {
    key: "asdp",
    icon: Puzzle,
    tint: "#3E9B96",
    title: "Autism profile (ASDP)",
    body: "For ticked pupils, track 5 developmental domains on a neurodiversity-affirming 1-9 scale, right beside the core subjects.",
  },
  {
    key: "selfeval",
    icon: Smile,
    tint: "#E0A33E",
    title: "Get to Know Me",
    body: "A gentle self-evaluation that captures each child's strengths, feelings and what helps them learn best.",
  },
  {
    key: "cos",
    icon: Layers,
    tint: "#7FB069",
    title: "Continuum of Support",
    body: "The heart of it all: build a living support plan per pupil (Classroom / School / School Support Plus) that pulls straight from your assessments, then track reviews over time.",
  },
  {
    key: "docs",
    icon: FileText,
    tint: "#4A5D6B",
    title: "Documents & reports",
    body: "Generate Support Plans, Review records, Targets sheets and full Reports. Every document has a Print button.",
  },
  {
    key: "meetings",
    icon: CalendarClock,
    tint: "#6C8EBF",
    title: "Meetings",
    body: "Plan parent or support meetings, draft an agenda with AI, capture notes and print the record.",
  },
  {
    key: "planning",
    icon: NotebookPen,
    tint: "#81B29A",
    title: "Planning & groups",
    body: "Keep shared class planning in one place and organise pupils into support groups across classes.",
  },
  {
    key: "overview",
    icon: Map,
    tint: "#C08552",
    title: "Overview & gaps",
    body: "See the whole class at a glance with the heatmap, class summary and gap analysis to spot who needs support.",
  },
  {
    key: "parents",
    icon: Share2,
    tint: "#9A7AA0",
    title: "Share with parents",
    body: "Send a secure, read-only link so parents can view their child's progress, with no login needed for them.",
  },
  {
    key: "settings",
    icon: ShieldCheck,
    tint: "#4A5D6B",
    title: "Settings & security",
    body: "Add your school details and logo, invite colleagues, and turn on two-factor sign-in from the top bar.",
  },
];

export default function HelpGuide({ onClose, onNavigate, onStartTour }) {
  const clickable = typeof onNavigate === "function";
  return (
    <div
      data-testid="help-guide-modal"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-[#F9F9F7] rounded-3xl shadow-2xl w-full max-w-2xl max-h-[88vh] flex flex-col animate-rise overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 px-6 sm:px-8 pt-6 pb-4 border-b border-slate-200/70 bg-white">
          <div className="flex items-center gap-3">
            <span className="h-11 w-11 rounded-2xl bg-[#81B29A] flex items-center justify-center text-white shadow-sm">
              <Sprout size={22} />
            </span>
            <div>
              <h3 className="font-['Outfit'] text-2xl font-bold text-slate-800 tracking-tight">How BloomTrack works</h3>
              <p className="text-sm text-slate-500 mt-0.5">A quick tour of everything you can do.</p>
            </div>
          </div>
          <button
            data-testid="help-guide-close-btn"
            onClick={onClose}
            className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-400 shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        <div className="overflow-y-auto px-6 sm:px-8 py-6">
          <div className="rounded-2xl bg-[#81B29A]/12 border border-[#81B29A]/25 p-5 mb-6">
            <p className="font-['Outfit'] font-semibold text-[#3f6b54]">Get started in 3 steps</p>
            <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">
              <span className="font-semibold text-slate-700">1)</span> Make a class &amp; add students &nbsp; &nbsp;
              <span className="font-semibold text-slate-700">2)</span> Assess each child 1-9 &nbsp; &nbsp;
              <span className="font-semibold text-slate-700">3)</span> Set priority targets and review. That's it, everything else builds on these.
            </p>
            {clickable && (
              <p className="text-xs text-[#3f6b54]/80 mt-2.5 font-semibold">Tip: tap any card below to jump straight to that feature.</p>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-3.5">
            {SECTIONS.map((s) => {
              const Icon = s.icon;
              const Wrapper = clickable ? "button" : "div";
              return (
                <Wrapper
                  key={s.key}
                  type={clickable ? "button" : undefined}
                  onClick={clickable ? () => onNavigate(s.key) : undefined}
                  data-testid={`help-section-${s.key}`}
                  className={`bg-white rounded-2xl border border-slate-100 p-4 flex gap-3.5 shadow-[0_2px_14px_rgba(42,59,71,0.04)] text-left w-full ${clickable ? "hover:-translate-y-0.5 hover:shadow-[0_6px_20px_rgba(42,59,71,0.10)] hover:border-slate-200 transition cursor-pointer" : ""}`}
                >
                  <span
                    className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${s.tint}1f`, color: s.tint }}
                  >
                    <Icon size={20} />
                  </span>
                  <div className="flex-1">
                    <h4 className="font-['Outfit'] font-semibold text-slate-800 leading-tight">{s.title}</h4>
                    <p className="text-sm text-slate-500 mt-1 leading-relaxed">{s.body}</p>
                  </div>
                  {clickable && <ChevronRight size={18} className="text-slate-300 self-center shrink-0" />}
                </Wrapper>
              );
            })}
          </div>

          <p className="flex items-center justify-center gap-1.5 text-xs text-slate-400 mt-6">
            <Printer size={13} /> Tip: look for the Print button on any document you'd like to file or hand out.
          </p>
        </div>

        <div className="px-6 sm:px-8 py-4 border-t border-slate-200/70 bg-white flex items-center justify-between gap-3">
          {onStartTour ? (
            <button
              data-testid="help-start-tour-btn"
              onClick={onStartTour}
              className="rounded-full px-5 py-2.5 font-semibold font-['Outfit'] border border-slate-200 bg-white text-slate-700 hover:border-[#81B29A] hover:text-[#3f6b54] transition inline-flex items-center gap-2 shrink-0"
            >
              <Compass size={16} /> Take a quick tour
            </button>
          ) : (
            <p className="text-xs text-slate-400">Reopen this guide anytime from the <span className="font-semibold text-slate-500">?</span> in the top bar.</p>
          )}
          <button
            data-testid="help-got-it-btn"
            onClick={onClose}
            className="bg-[#4A5D6B] hover:bg-[#3A4A56] text-white rounded-full px-6 py-2.5 font-semibold font-['Outfit'] transition shadow-sm hover:shadow-md shrink-0"
          >
            Got it - let's go
          </button>
        </div>
      </div>
    </div>
  );
}
